import RiotControl from 'riotcontrol'
import cookie from 'cookie-cutter'
import logger from '../logger'
import {
  merge,
  get,
} from 'lodash-es'
import extendedObservable from './base'
import api from '../api'
import AjaxUtils from '../utils/ajax-utils'
import { OrderObservableInstance } from './order-observable'
import { LineItemsInstance } from './line-items'


// //////////////////
// Billing Address //
// //////////////////

class BillingAddress {
  constructor() {
    const orderResourceKey = 'billingAddress'

    extendedObservable(this, 'billingAddressModel')

    // handles the edge case when there's a conflict between billing address and contact info
    // that prevents persisting the address.
    const handleConflict = current => new Promise((resolve) => {
      const fixConflict = (address) => {
        resolve(merge(current, { address })) // retry
      }
      RiotControl.trigger('modalFactory.create', {
        title: 'Something went wrong with your information, please address below.',
        template: 'invalid-address-info-modal',
        noClose: true,
        data: {
          billingAddress: current.address,
        },
        buttons: [{
          title: 'Update my info',
          type: 'submit',
          el_classes: 'btn btn-primary',
          on_click: fixConflict,
          on_keypress: fixConflict,
        }],
      })
    })

    const successHandler = (json, status, xhr) => {
      logger('Success: Communicated with OP Service - ', 'info')()
      logger(json, 'info')()

      this.model = merge(this.model, json)
      RiotControl.trigger('updateOrderResource', orderResourceKey, this.model)

      // update the order checksum
      const checksum = xhr.getResponseHeader('checksum')
      RiotControl.trigger('updateOrderChecksum', checksum)

      return this.model
    }

    const errorHandler = (xhr, status, errorThrown) => new Promise((resolve, reject) => {
      const invalidData = JSON.parse(JSON.stringify(this.model))
      const phoneCodes = [400, 401, 402]
      const fieldErrors = [409]
      const invalidPhoneError = (Array.isArray(xhr.responseJSON.error))
        ? xhr.responseJSON.error.some(e => phoneCodes.includes(e.code))
        : false
      const invalidFieldError = (Array.isArray(xhr.responseJSON.error))
        ? xhr.responseJSON.error.some(e => fieldErrors.includes(e.code))
        : false
      const errorCode = get(xhr, 'responseJSON.error["0"].code')

      // If checksum is invalid
      if (AjaxUtils.checkForBadChecksum(xhr)) {
        AjaxUtils.handleChecksumMismatch()
      }

      if (AjaxUtils.checkForNoOrder(xhr)) {
        AjaxUtils.handleNoOrder()
      }

      // phone too long
      if (errorCode === 401) {
        // handle the too long phone number error
        RiotControl.trigger('updateOrderResource', 'orderIssues', ['Billing: Phone too long'])
      }

      logger(`Error: Failed to communicate with OP Service - ${errorThrown}`, 'error')()
      logger(`Status: ${status}`, 'error')()
      logger(xhr, 'error')()

      if (invalidPhoneError || invalidFieldError) {
        handleConflict(invalidData)
          .then((addressData) => {
            // try one more time otherwise fail totally
            api.billingAddress.put(JSON.stringify(addressData))
              .then(() => {
                resolve()
              })
              .fail((x) => {
                reject(x)
              })
          })
          .catch((fail) => {
            reject(fail)
          })
          .then(() => {
            // hiding the global overlay screen after async stuff happens
            RiotControl.trigger('hideGlobalOverlay')
          })
      } else {
        // hiding the global overlay screen after async stuff happens
        RiotControl.trigger('hideGlobalOverlay')
        reject(xhr)
      }
    })

    this.updateBillingAddress = data => new Promise((resolve, reject) => {
      // Merge the changed data onto the order.
      this.model = merge(this.model, data)

      // Stringify the request
      const requestData = JSON.stringify(this.model)

      api.billingAddress.put(requestData)
        .then(successHandler)
        .then(resolve)
        .fail((xhr, status, errorThrown) => {
          errorHandler(xhr, status, errorThrown)
            .then(resolve)
            .catch(reject)
        })
    })

    this.on('updateBillingAddress', (data, cb, errorCb, preventAudit) => {
      logger('updateBillingAddress', 'debug')()
      this.updateBillingAddress(data)
        .then(LineItemsInstance.conditionallyUpdateShipping)
        .then(() => { // eslint-disable-line consistent-return
          RiotControl.trigger('updateAppModel', 'skipAuditOnTransition', true)
          if (!preventAudit) return OrderObservableInstance.auditOrder()
        })
        .then(cb)
        .catch((err) => {
          logger(err, 'debug')()
          if (typeof errorCb === 'function') {
            errorCb(err)
          }
        })
    })

    this.on('update', (data) => {
      if (this.model !== data.orderModel[orderResourceKey]) {
        this.model = merge(this.model, data.orderModel[orderResourceKey])
      }
    })

    // Set a cookie when the user confirms the billing the address they want to use is different
    // from the billing address in their account
    this.on('setBillingAddressCookie', () => {
      // Set cookie to expire in 2 weeks
      const expires = new Date(Date.now() + 1209600000).toGMTString()
      cookie.set('useOrderBilling', true, { expires })
    })
  }
}

export const BillingAddressInstance = new BillingAddress();
